/**
 * Hupper FPS Arena — PlayCanvas boot.
 *
 * Sprint 0.3: connects to Colyseus game-server, spawns local player +
 * remote players, syncs at 64Hz. Open two browser tabs at localhost:3002
 * and they should see each other as colored capsules moving in the arena.
 */
import * as pc from "playcanvas";
import { MATCH_FORMAT } from "@hupper/shared";
import { AssetGate, preloadImages } from "./AssetGate";
import { Arena } from "./scene/Arena";
import { setupEnvironment } from "./scene/Environment";
import { FirstPersonController } from "./player/FirstPersonController";
import { GrenadeController } from "./player/GrenadeController";
import { LocalWeaponView } from "./player/LocalWeaponView";
import { GameClient } from "./net/GameClient";
import { PracticeBot } from "./net/PracticeBot";
import { isPracticeMode } from "./practiceMode";
import { RemotePlayerManager } from "./player/RemotePlayerManager";
import { CharacterFactory } from "./player/CharacterFactory";
import { VsIntro } from "./intro/VsIntro";
import { HalftimeCinematic } from "./intro/HalftimeCinematic";
import { MatchEndCinematic } from "./intro/MatchEndCinematic";
import { LocalPlayerShadow } from "./player/LocalPlayerShadow";
import { BuyMenu } from "./ui/BuyMenu";
import { ChatBox } from "./ui/ChatBox";
import { Crosshair } from "./ui/Crosshair";
import { CrosshairSettings } from "./ui/CrosshairSettings";
import { GameSettings } from "./ui/GameSettings";
import { loadSettings, subscribeSettings } from "./store/settingsStore";
import { ScopeOverlay } from "./ui/ScopeOverlay";
import { Scoreboard } from "./ui/Scoreboard";
import { HudBottomCenter } from "./ui/HudBottomCenter";
// Sprint 5 — combat feedback effects.
import { HitMarker } from "./effects/HitMarker";
import { DamageVignette } from "./effects/DamageVignette";
import { DamageDirection } from "./effects/DamageDirection";
import { BloodSplatter } from "./effects/BloodSplatter";
import { Tracer } from "./effects/Tracer";
// Sprint 7 — server-authoritative grenade visuals.
import { PeerGrenadeRenderer } from "./effects/PeerGrenadeRenderer";
// Dropped weapons on the ground (G to drop / E to pick up).
import { GroundItemRenderer } from "./effects/GroundItemRenderer";
import { PickupPrompt } from "./ui/PickupPrompt";
// Sprint 6 — HUD + competitive features.
import { KillFeed } from "./ui/KillFeed";
import { WeaponInventoryHud } from "./ui/WeaponInventoryHud";
import { isTouchMode } from "./input/touchMode";
import { TouchControls } from "./input/TouchControls";
import { TabScoreboard } from "./ui/TabScoreboard";
import { WEAPON_ICON_IMG_URLS } from "./ui/weaponIcons";
// RoundTimer (top-right card) replaced by the inline timer inside the
// Scoreboard's round-info slot. Old file preserved in src/legacy/ui/.
import { SpectatorCam } from "./ui/SpectatorCam";
import { MatchEndOverlay } from "./ui/MatchEndOverlay";
import { ConnectionOverlay } from "./ui/ConnectionOverlay";
import { SearchingOverlay } from "./ui/SearchingOverlay";
// Sound (test phase) — positional footsteps + gunfire + buy-phase beep.
import { Footsteps } from "./audio/Footsteps";
import { WeaponSounds } from "./audio/WeaponSounds";
import { GrenadeSounds } from "./audio/GrenadeSounds";
import { KnifeSounds } from "./audio/KnifeSounds";
import { PlayerSounds } from "./audio/PlayerSounds";
import { UISounds } from "./audio/UISounds";
import { RoundWinSounds } from "./audio/RoundWinSounds";
import { AudioMuffle } from "./audio/AudioMuffle";
import { CountdownBeep } from "./audio/CountdownBeep";

const canvas = document.getElementById("game-canvas") as HTMLCanvasElement;
if (!canvas) throw new Error("Could not find #game-canvas");

// ── Boot PlayCanvas ─────────────────────────────────────────────
const app = new pc.Application(canvas, {
  mouse:    new pc.Mouse(canvas),
  keyboard: new pc.Keyboard(window),
  touch:    new pc.TouchDevice(canvas),
  graphicsDeviceOptions: { antialias: true, alpha: false },
});

app.setCanvasResolution(pc.RESOLUTION_AUTO);
app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
// Cap the render resolution: on Retina/4K, RESOLUTION_AUTO renders at
// devicePixelRatio² pixels (2× DPR = 4× the pixels of 1080p), which with
// shadows + HDRI + many lights is the main perf hit. Clamp to 1.5× — big
// fill-rate savings, barely perceptible softening. Raise toward 2 if perf
// allows, drop to 1 for the most headroom.
app.graphicsDevice.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
window.addEventListener("resize", () => app.resizeCanvas());

// ── Master volume ───────────────────────────────────────────────
// The settings gear writes the player's chosen master volume into the
// store; apply it (and every live change) to the PlayCanvas SoundManager,
// whose global `volume` scales every sound instance. Set up before any
// audio plays so the first sounds already honor the saved level.
const applyMasterVolume = (v: number): void => {
  const sm = (app as unknown as { soundManager?: { volume?: number } }).soundManager;
  if (sm) sm.volume = Math.max(0, Math.min(1, v));
};
applyMasterVolume(loadSettings().masterVolume);
subscribeSettings((s) => applyMasterVolume(s.masterVolume));

// ── Asset-load gate ─────────────────────────────────────────────
// Constructed BEFORE any loader so its registry error listener sees every
// load from the first one; awaited at the bottom of boot (after all loaders
// have enqueued) to report `client_ready` for the match-start gate.
const assetGate = new AssetGate(app);

// ── World ───────────────────────────────────────────────────────
const arena = new Arena(app);
arena.build();

// ── Networking ──────────────────────────────────────────────────
// Launch params come from the embedding web app's iframe URL:
//   ?wallet=0x..&stake=5&server=wss://game.hupper.fun&token=…
// Falls back to env + anon for standalone dev (two tabs on :3002).
const launch = new URLSearchParams(window.location.search);
const walletAddress = launch.get("wallet") || undefined;
const stakeAmount = launch.get("stake") || "1";
// Hupper JWT — handed to the Colyseus room so it can verify (in onAuth)
// that the player owns the wallet claim. Empty in standalone dev → the
// room falls through to its anon path when JWT_SECRET is unset.
const apiToken = launch.get("token") || undefined;
const SERVER_URL =
  launch.get("server") ||
  (import.meta as any).env.VITE_GAME_SERVER_URL ||
  "ws://localhost:2567";

// Real identity from the embedding web app (the player's chosen Hupper
// handle + uploaded avatar photo). The host forwards them via the iframe
// URL; both surface on the Tab scoreboard. Falls back to a wallet-derived
// tag / anon for standalone dev where there's no logged-in user.
const launchName = launch.get("name")?.trim();
const avatarUrl = launch.get("avatar") || undefined;
const playerName =
  launchName ||
  (walletAddress
    ? `p-${walletAddress.slice(2, 6)}`
    : `anon-${Math.floor(Math.random() * 9999)}`);

// When the host's reconnect card relaunches us it carries the seat's
// reconnection token — rejoin that seat instead of queueing a new match.
const reconnectToken = launch.get("reconnect") || undefined;

// Set for a private friend-vs-friend custom match — forwarded to the room's
// filterBy so only the two invited players co-seat (the open queue omits it).
const customMatchId = launch.get("customMatchId") || undefined;

// Set for a tournament bracket match — `<tournamentId>:<matchId>`. Same idea
// as customMatchId: seats the two specific opponents, and the server skips
// per-match escrow (the entry was pooled when they registered).
const tournamentMatchId = launch.get("tournamentMatchId") || undefined;

// ── Practice mode (free 1v1 vs the in-browser bot) ───────────────
// Flag lives in ./practiceMode (module-scope: the settings/crosshair stores
// read it at import time to pick their practice-scoped storage keys). If the
// practice join fails (older server without fps_practice, or the practice
// cap), the catch below falls back to exactly the old offline behavior.
const practiceMode = isPracticeMode;

// The practice bot's second Colyseus connection decodes the room's (unused)
// schema state and floods the console with @colyseus/schema chatter at
// snapshot rate ("definition mismatch", '"refId" not found', …) — enough
// volume to bury real logs and cost frame time. Same surgical string filter
// scripts/bot.mjs uses on its terminal, installed ONLY on the standalone
// practice page (embedded/paid pages keep their console untouched).
if (practiceMode) {
  const noisy = (a: unknown): boolean =>
    typeof a === "string" &&
    (a.includes("definition mismatch") ||
      a.includes("field not defined") ||
      a.includes('"refId" not found') ||
      a.includes("utf8Read()") ||
      a.includes("Please report this issue"));
  for (const ch of ["warn", "log", "error"] as const) {
    const real = console[ch].bind(console);
    console[ch] = (...args: unknown[]) => {
      if (!noisy(args[0])) real(...args);
    };
  }
}

// ── Fullscreen on match start ────────────────────────────────────
// Mirrors the other Hupper games (Pong/Snake): take the viewport the
// moment the opponent is found, release it on the way back to the lobby
// (the parent calls exitFullscreen when it receives `hupper-fps-exit`,
// and unmounting the iframe drops fullscreen automatically too).
//
// The iframe carries allow="fullscreen", so requesting it on the iframe's
// own documentElement fills the screen with the arena. requestFullscreen
// needs a user gesture, so the match-found call is best-effort and a
// pointerdown fallback rides the player's first click (the same click
// that locks the pointer) into fullscreen on gesture-gated browsers.
let matchLive = false;
let triedFullscreen = false;
// Reconnect-seat reporting. We capture the room's reconnect token on bind
// but only tell the host the seat is "active" once a REAL match starts
// (phase leaves "warmup"). Reporting on bind made the host persist a
// reconnect record — and pop the floating reconnect card — while the
// player was still just searching for an opponent (no match yet).
let boundInfo: { roomId: string; reconnectionToken: string } | null = null;
let activeReported = false;
function enterFullscreen(): void {
  if (document.fullscreenElement) return;
  document.documentElement.requestFullscreen?.().catch(() => {
    /* no gesture / blocked — the pointerdown fallback will retry */
  });
}
canvas.addEventListener("pointerdown", () => {
  if (matchLive && !document.fullscreenElement) enterFullscreen();
});

/** Post a message up to the embedding web app (no-op when standalone). */
function postToHost(payload: unknown): void {
  try {
    if (window.parent && window.parent !== window) window.parent.postMessage(payload, "*");
  } catch {
    /* standalone / cross-origin without access */
  }
}

// Tell the host the iframe document loaded + JS is running, so it can tell
// "booted, still connecting" apart from "iframe failed to load" (connection
// refused / wrong VITE_FPS_APP_URL) and show a clear message instead of the
// browser's raw gray error page.
postToHost({ type: "hupper-fps-booted" });

let client: GameClient | null = null;

// Real, timer-less matchmaking indicator. Shown immediately on a fresh
// match (skipped when relaunching into an existing seat via reconnect) and
// hidden once a second player joins (phase leaves warmup). Practice passes
// "0" — the overlay already suppresses its stake sub-line for that value.
const searchingOverlay = new SearchingOverlay();
if (!reconnectToken) searchingOverlay.show(practiceMode ? "0" : stakeAmount);

/** Small always-on banner for practice matches: no money is at stake here,
 *  and the real game lives at hupper.fun. English copy (product rule). */
function showPracticeBadge(): void {
  const el = document.createElement("div");
  el.id = "practice-badge";
  el.style.cssText = [
    "position:fixed",
    "left:12px",
    "bottom:12px",
    "z-index:9996",
    "padding:6px 10px",
    "border-radius:8px",
    "background:rgba(10,10,14,0.72)",
    "color:rgba(255,255,255,0.85)",
    "font:600 11px/1.4 system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
    "letter-spacing:0.04em",
    "pointer-events:auto",
  ].join(";");
  el.innerHTML =
    `PRACTICE MATCH · vs bot · nothing at stake — ` +
    `<a href="https://hupper.fun" target="_top" rel="noopener" ` +
    `style="color:#FF8A4A;text-decoration:underline">play for real at hupper.fun</a>`;
  document.body.appendChild(el);
}

let practiceBot: PracticeBot | null = null;

(async () => {
  try {
    client = new GameClient(SERVER_URL);
    // Capture the room's reconnect token on bind, but DON'T report the seat
    // as active yet — that happens in onMatchHud once a real match starts
    // (see boundInfo / activeReported above). The exception: if we've
    // already reported active this session, a re-bind means the token
    // rotated on a live reconnect — push the fresh token up immediately so
    // the host's saved record stays valid.
    client.onBound((info) => {
      boundInfo = info;
      if (activeReported) postToHost({ type: "hupper-fps-active", ...info });
    });
    if (reconnectToken) {
      await client.reconnectWith(reconnectToken);
      console.log(`[fps] reconnected to ${SERVER_URL} via host card`);
    } else if (practiceMode) {
      // Free practice: join the anon fps_practice room under a random
      // pairing id, then spawn the in-browser bot as the second seat.
      // Standalone-only by construction (see practiceMode above), so the
      // host postMessage surface stays a no-op throughout.
      const practiceId = `pr-${crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
      await client.connectPractice(playerName, practiceId);
      console.log(`[fps] practice match vs bot · server=${SERVER_URL}`);
      practiceBot = new PracticeBot(SERVER_URL, practiceId, arena.getCollisionBoxes());
      // Bot join failure (cap raced, server hiccup) must not strand the
      // human on the searching screen forever — drop to offline like any
      // other connect failure.
      await practiceBot.connect();
      showPracticeBadge();
      // Standalone: the overlays' "Back to lobby" / CANCEL exits normally
      // post `hupper-fps-exit` to the embedding host. Here the page IS the
      // top window, so catch the self-posted message and restart into a
      // fresh practice match.
      window.addEventListener("message", (e) => {
        const t = (e.data as { type?: string } | null)?.type;
        if (t === "hupper-fps-exit") window.location.reload();
      });
    } else {
      await client.connect(playerName, walletAddress, stakeAmount, apiToken, avatarUrl, customMatchId, tournamentMatchId);
      console.log(
        `[fps] connected to game-server ${SERVER_URL} · stake=$${stakeAmount} · wallet=${walletAddress ?? "anon"}` +
          (customMatchId ? ` · custom=${customMatchId}` : "") +
          (tournamentMatchId ? ` · tournament=${tournamentMatchId}` : ""),
      );
    }
  } catch (err) {
    console.warn("[fps] connect/reconnect failed — running offline", err);
    searchingOverlay.hide(); // nothing to search for while offline
    // Half-started practice (human seated but the bot join failed): tear
    // both sides down so the page lands in the plain offline walk-around
    // instead of a botless warmup that never becomes a match.
    practiceBot?.dispose();
    practiceBot = null;
    if (practiceMode) {
      void client?.disconnect().catch(() => { /* socket already gone */ });
      client = null;
    }
    // Drop the stale reconnect card on the host if a rejoin failed.
    if (reconnectToken) postToHost({ type: "hupper-fps-match-end" });
  }

  // Local player + camera (static collision geometry + ramp surfaces)
  const player = new FirstPersonController(app, canvas, client, arena.getCollisionBoxes(), arena.getRamps());

  // Touch devices (iPad / phones): no Pointer Lock API exists there, so the
  // desktop mouse flow can't fire or aim. On-screen controls take over —
  // left-zone joystick + look drag + FIRE/JUMP/R/ZOOM/NADE/BUY buttons.
  // A hardware keyboard (iPad Magic Keyboard) keeps working alongside.
  // Desktop is untouched; `?touch=1` previews the layout with a mouse.
  if (isTouchMode()) new TouchControls(canvas, player, client);

  // HDRI image-based lighting + sky (brightens the arena; needs the camera).
  setupEnvironment(app, player.getCameraEntity());

  // Dynamic crosshair — gap expands with inaccuracy (Sprint 3).
  new Crosshair(player);

  // Top-right gear: mouse sensitivity + master volume (persisted). Always
  // available (even offline) — sensitivity feeds the controller, volume the
  // SoundManager, both through the settings store's subscribe channel.
  new GameSettings();

  // AWP scope — circular lens + hairlines while zoomed (RMB on a sniper).
  new ScopeOverlay(player);

  // Shared skinned-character loader — opponents, the local shadow body, and
  // the VS intro all stamp their characters from this one set of loaded models.
  const characters = new CharacterFactory(app);
  // One-time pre-buy "VS" cinematic: the two fighters stand on the mid platform
  // while the random sides are revealed and a 10s timer counts down. It drives
  // itself off the match phase; `?vsintro=1` runs a standalone visual preview.
  const vsIntro = new VsIntro(app, client, player, characters);
  if (launch.get("vsintro")) vsIntro.debugStart();

  // One-time halftime side-swap cinematic: the local player's mannequin on
  // its NEW side + faction title while the server holds the `halftime`
  // phase. `?halftime=1` (CT) / `?halftime=t` previews it standalone;
  // `&hfpose=stretch|rifle_walk_stop` forces a pose (random otherwise).
  const halftimeCinematic = new HalftimeCinematic(app, client, player, characters);
  const halftimePreview = launch.get("halftime");
  if (halftimePreview) {
    const hfpose = launch.get("hfpose");
    halftimeCinematic.debugStart(
      halftimePreview === "t" ? "t" : "ct",
      hfpose === "stretch" || hfpose === "rifle_walk_stop" ? hfpose : undefined,
    );
  }

  // End-of-match result screen: the local player's posed mannequin (left) +
  // a stats panel (right) + win/loss & money (bottom). Created here (not
  // inside the `if (client)` below) so `?matchend=1` / `?matchend=lose` can
  // preview it without a live match.
  const matchEndCinematic = new MatchEndCinematic(app, player, characters);
  const matchEndOverlay = new MatchEndOverlay();
  const matchEndPreview = launch.get("matchend");
  if (matchEndPreview) {
    matchEndCinematic.show("ct", matchEndPreview !== "lose");
    matchEndOverlay.show(
      { won: matchEndPreview !== "lose", winnerSessionId: "preview", stake: 5, payout: 9.5, fee: 0.5, escrowed: false },
      { kills: 12, deaths: 7, durationSec: 754, name: "Nova", team: "ct" },
    );
  }

  // Bootstrap own player from server snapshot, then trust prediction
  // (Phase 1 will reconcile via input replay on lastProcessedInputSeq)
  if (client) {
    client.onPlayerAdd((snap, sessionId) => {
      if (sessionId === client?.sessionId) {
        // Yaw must be applied BEFORE position so the bootstrap flag flips
        // in the right order inside FirstPersonController.
        player.syncYawFromServer(snap.yaw);
        player.setServerPosition(snap.x, snap.y, snap.z);
        console.log(`[fps] bootstrapped own spawn: pos=(${snap.x.toFixed(1)}, ${snap.y.toFixed(1)}, ${snap.z.toFixed(1)}) yaw=${snap.yaw}`);
      } else {
        console.log(`[fps] remote spawned: ${sessionId} at (${snap.x.toFixed(1)}, ${snap.y.toFixed(1)}, ${snap.z.toFixed(1)})`);
        // Opponent seated while the room holds in warmup (the pre-match
        // load gate) — tell the searching card the match was found and is
        // loading. No-op when the card is hidden (reconnect relaunch).
        searchingOverlay.setMatchFound(true);
      }
    });

    client.onPlayerRemove((_snap, sessionId) => {
      console.log(`[fps] player left: ${sessionId}`);
      // Opponent bailed during the pre-match load window (room still in
      // warmup) — back to searching; the seat reopened on the server.
      if (sessionId !== client?.sessionId && client?.getMatchPhase() === "warmup") {
        searchingOverlay.setMatchFound(false);
      }
    });

    const remotePlayers = new RemotePlayerManager(app, client, characters);
    // First-person own shadow: a shadow-only body that casts into the sun's
    // shadow map so you see your shadow when looking down (never the body).
    new LocalPlayerShadow(app, client, player, characters);
    new Scoreboard(client);
    new BuyMenu(client);
    // CS:GO-style HUD: centered vitals (health · star · ammo) +
    // bottom-right weapon/utility slot list.
    new HudBottomCenter(client);
    // Touch mode: the slot rows double as tap targets (no 1/2/3/4 keys).
    new WeaponInventoryHud(client, isTouchMode() ? (slot) => player.equipSlot(slot) : undefined);

    // Sprint 5 — view-model with bob state machine + F-inspect.
    const weaponView = new LocalWeaponView(app, player.getCameraEntity(), client, player);
    // Lock fire input while the view-model is mid-switch / mid-inspect.
    player.setInputBlocker(() => weaponView.isBusy());
    // Hide the viewmodel while the AWP scope is up (CS:GO).
    weaponView.setScopeProbe(() => player.isScoped());

    const grenadeController = new GrenadeController(app, client, player);
    weaponView.setGrenadeProbe(() => grenadeController.getChargeState());
    // Sprint 5 — wire the shared shake bus into the camera. Both grenade
    // explosions and damage-taken pulses route through it.
    const shakeBus = grenadeController.getEffects();
    player.setShakeSource(shakeBus);

    // Sprint 7 — server-driven grenade visuals (peer marbles +
    // persistent smoke/fire zones + detonate effect spawn). The local
    // GrenadeController only sends `grenade_throw`; everything visible
    // flows through the broadcasts.
    new PeerGrenadeRenderer(app, client, shakeBus);

    // Flashbang blind is server-authoritative (LOS through walls + view
    // angle + distance computed server-side) — just render the white-out.
    client.onFlashBlind((m) => shakeBus.applyBlind(m.peak, m.holdMs, m.fadeMs));

    // Breakable glass — server-authoritative. The `glass_break` event hides
    // the pane + plays the shatter VFX (SFX is wired in GrenadeSounds). The
    // pane re-shows at the start of every round; late-join / dropped-event
    // clients self-heal off the snapshot's `glassBroken` flag.
    client.onGlassBreak((m) => {
      arena.setGlassBroken(true);
      player.setGlassBroken(true); // stop colliding with the now-shattered pane
      shakeBus.shatter(new pc.Vec3(m.x, m.y, m.z));
    });

    // Drop / pickup — render every dropped weapon the server reports
    // and surface a "[E] Pick up" hint when the crosshair is over one.
    // The server owns physics + ownership; this is purely visual + UI.
    const groundItems = new GroundItemRenderer(app, client);
    new PickupPrompt(app, player.getCameraEntity(), groundItems);

    // Sprint 5 — visual + UI feedback layer.
    new HitMarker(client);
    new DamageVignette(client);
    new DamageDirection(client, player.getCameraEntity());
    new BloodSplatter(app, client, arena.getCollisionBoxes());
    // Peer tracers start at the shooter's rendered hand/weapon (resolved
    // from the remote players) instead of the eye — bullets exit the gun.
    new Tracer(app, client, arena.getCollisionBoxes(), (sid, out) =>
      remotePlayers.getHandWorld(sid, out),
    );

    // Damage-taken screen shake — amount scales with damage delta.
    client.onHit((msg) => {
      if (msg.victimSessionId !== client?.sessionId) return;
      const dmg = Math.max(0, Math.min(120, msg.damage));
      const mag = 0.6 + (dmg / 120) * 2.4; // 0.6° → 3.0° kick
      shakeBus.addShake(
        (Math.random() - 0.5) * 2 * mag,
        (Math.random() - 0.5) * 2 * mag,
      );
    });

    // Sprint 6 — HUD polish + competitive features.
    const killFeed = new KillFeed(client);
    if (launch.get("killdemo")) killFeed.debugPreview();
    const tabScoreboard = new TabScoreboard(client);    // hold Tab — both players, ping, money
    if (launch.get("reportdemo")) tabScoreboard.debugPreview();
    // In-match text chat (press T). Opening releases held movement keys —
    // from then on the controller's keydown gate starves their keyup, and
    // without the release a W held across the open would run forever.
    const chatBox = new ChatBox(client, () => player.releaseHeldKeys());
    if (launch.get("chat")) chatBox.debugPreview();
    // Round timer now lives inside Scoreboard's centered round-info slot.
    new CrosshairSettings();      // press C
    new SpectatorCam(app, client, player);
    // Master "concussion" bus — installs the global low-pass splice before
    // any sound plays, so it can muffle the whole mix when an HE detonates
    // near the local player (wired through onHit below).
    const audioMuffle = new AudioMuffle(app);
    new Footsteps(app, client, player); // positional walk sound (test phase)
    const weaponSounds = new WeaponSounds(app, client, player.getCameraEntity()); // gunfire + reload
    player.onZoom(() => weaponSounds.playZoom()); // AWP scope-toggle SFX
    new GrenadeSounds(app, client, player.getCameraEntity()); // grenade draw/explode/bounce + fire-zone loop
    new KnifeSounds(app, client, player.getCameraEntity()); // knife deploy / hit / hitwall
    new PlayerSounds(app, client, player.getCameraEntity()); // death grunts + burn pain
    new UISounds(app, client); // buy + weapon/grenade pickup
    new RoundWinSounds(app, client); // "Counter-Terrorists / Terrorists Win" announcer
    new CountdownBeep(app, client); // last-3s buy-phase beep

    // CS:GO-style HE concussion — taking HE damage nearby muffles your
    // hearing + rings your ears. The blast `damage` the server already
    // attenuated by distance / walls / armor drives the daze strength, so
    // a point-blank HE dazes hard and a far or blocked one barely registers.
    const HE_DAZE_REF_DAMAGE = 55; // damage that yields a full-strength daze
    client.onHit((msg) => {
      if (msg.victimSessionId !== client?.sessionId) return;
      if (msg.weaponId !== "he" || msg.damage <= 0) return;
      audioMuffle.daze(Math.min(1, msg.damage / HE_DAZE_REF_DAMAGE));
    });

    // Local fallback for the end-screen match timer: stamp when the match
    // leaves warmup. The server's match_end `durationSec` wins when present
    // (it survives reconnects); this only covers older servers.
    let localMatchStartMs = 0;
    // Hide the "searching for opponent" overlay the moment the match
    // starts — the server flips phase off "warmup" once a 2nd player joins.
    client.onMatchHud((hud) => {
      if (hud.phase !== "warmup") {
        if (localMatchStartMs === 0) localMatchStartMs = Date.now();
        searchingOverlay.hide();
        // A real match is underway (opponent found, past warmup) → NOW it's
        // worth a reconnect seat. Report it to the host exactly once so the
        // floating reconnect card only ever represents a started match,
        // never the matchmaking/searching state.
        if (!activeReported && boundInfo) {
          activeReported = true;
          postToHost({ type: "hupper-fps-active", ...boundInfo });
        }
        // Opponent found → take the viewport. Best-effort here; the
        // canvas pointerdown fallback guarantees it on gesture-gated
        // browsers (the player clicks to lock the pointer right away).
        if (!triedFullscreen) {
          triedFullscreen = true;
          matchLive = true;
          enterFullscreen();
        }
      }
    });

    // Reconnect grace feedback — self reconnecting + opponent waiting.
    const connectionOverlay = new ConnectionOverlay();
    client.onReconnecting(() => connectionOverlay.showReconnecting());
    client.onReconnected(() => connectionOverlay.hide());
    client.onReconnectFailed(() => connectionOverlay.showLost());
    client.onOpponentPaused(({ graceSec }) => connectionOverlay.showOpponentWaiting(graceSec));
    client.onOpponentResumed(() => connectionOverlay.hide());
    // The hide messages above are one-shot: missed while our own socket
    // blinks → overlay stuck over the whole match. Snapshots re-sync on
    // reconnect, so let them heal the transient states too.
    client.onMatchHud((h) => connectionOverlay.selfHeal(h.phase));

    // End-of-match handoff to the embedding web app. The result overlay +
    // cinematic were created above (so `?matchend` can preview them); here
    // they're driven off the real result once the grace window elapses.
    client.onMatchEnd((msg) => {
      connectionOverlay.hide();
      // Match decided — stop the pointerdown fallback from re-grabbing
      // fullscreen while the result screen is up. The actual exit happens
      // when the player clicks "Back to lobby" (`hupper-fps-exit`).
      matchLive = false;
      // Duration is decided NOW (message arrival), not after the grace
      // window — the aftermath isn't part of the match.
      const durationSec =
        msg.durationSec ??
        (localMatchStartMs > 0 ? Math.round((Date.now() - localMatchStartMs) / 1000) : 0);
      // Hold the result back for the post-match grace window so the final
      // grenade fire / smoke / explosions play out first; the server keeps
      // the world simulating for the same window.
      window.setTimeout(() => {
        const self = client?.getLatestSelf();
        const team: "ct" | "t" = self?.team === "t" ? "t" : "ct";
        matchEndCinematic.show(team, msg.won);
        matchEndOverlay.show(msg, {
          kills: self?.kills ?? 0,
          deaths: self?.deaths ?? 0,
          durationSec,
          name: self?.name,
          team,
        });
      }, MATCH_FORMAT.matchEndGraceSeconds * 1000);
    });
    client.onMatchSettled(() => matchEndOverlay.setSettleResult("ok"));
    client.onSettleFailed((reason) => matchEndOverlay.setSettleResult("failed", reason));
    client.onLockFailed((reason) => {
      searchingOverlay.hide();
      try {
        window.parent?.postMessage({ type: "hupper-fps-lock-failed", reason }, "*");
      } catch {
        /* standalone — no embedding parent */
      }
    });
  }

  // Reconcile local prediction toward the server's authoritative position on
  // every snapshot (not just on change — stationary drift used to slip through
  // the dedup), and re-snap to spawn at the start of each new round (the soft
  // Y-only reconcile never catches X/Z up on its own).
  if (client) {
    // Re-sync the local aim/movement basis whenever the server respawns us.
    // On respawn the server resets yaw→spawn and pitch→0 (plus position); if
    // the client keeps its pre-death yaw, client and server hold a constant
    // yaw offset — WASD then drifts diagonally and shots (fired from the
    // server's yaw) miss the crosshair. Two respawn signals, because no single
    // one covers every phase:
    //   1. health 0→positive edge — catches round respawns, where the dead
    //      player sits at health 0 for the whole between-rounds window.
    //   2. warmup position snap-back — a warmup self-death (point-blank HE /
    //      molotov) respawns in the SAME server tick the fatal hit lands
    //      (respawnWarmupPlayer), so health is already 100 again in the next
    //      broadcast and signal (1) never sees the 0. The respawn still
    //      teleports us from wherever we died back to spawn, so a hard-snap-
    //      sized gap between our prediction and the server position is the
    //      reliable signal. Gated to warmup: live/between-round resets go
    //      through onLiveRoundStarted and we must never yank aim mid-combat.
    // Either way, respawnAt re-syncs yaw/pitch/pos from the same snapshot
    // (the server resets them atomically, so this snapshot carries the spawn
    // values).
    let prevSelfHealth = 100;
    client.onLocalSnapshot((snap) => {
      const revived = prevSelfHealth <= 0 && snap.health > 0;
      prevSelfHealth = snap.health;

      let warmupRespawn = false;
      if ((client?.getMatchPhase() ?? "live") === "warmup") {
        const p = player.getBodyEntity().getPosition();
        const dx = snap.x - p.x;
        const dz = snap.z - p.z;
        warmupRespawn = dx * dx + dz * dz > 1.5 * 1.5;
      }

      // Self-heal the breakable glass off the world-state flag so late-join
      // / dropped-event clients converge (never client-raycast — server owns
      // the break; this only reflects the authoritative state).
      arena.setGlassBroken(!!snap.glassBroken);
      player.setGlassBroken(!!snap.glassBroken);

      if (revived || warmupRespawn) {
        player.respawnAt(snap.x, snap.y, snap.z, snap.yaw, snap.pitch);
        return;
      }
      // Anchored on the server's input ack — see reconcileFromServer.
      player.reconcileFromServer(snap.x, snap.y, snap.z, snap.lastProcessedInputSeq);
    });

    client.onLiveRoundStarted(() => {
      // Glass resets to intact each round (server re-blocks it too).
      arena.setGlassBroken(false);
      player.setGlassBroken(false);
      const self = client?.getLatestSelf();
      if (!self) return;
      player.respawnAt(self.x, self.y, self.z, self.yaw, self.pitch);
    });

    // Re-show the intact pane when a round ends, so the shattered pane
    // doesn't linger into the post-round / next-round transition.
    client.onRoundEnd(() => { arena.setGlassBroken(false); player.setGlassBroken(false); });
  }

  app.start();
  console.log("[fps] Sprint 0.3 boot OK — multiplayer-ready");

  // Every loader above has enqueued its downloads (PlayCanvas registry:
  // arena textures + props, HDRI, character + anim GLBs, all audio; DOM:
  // the HUD's rendered PNG icons). Once they all settle, report ready —
  // the server holds the match start (warmup → intro) until BOTH clients
  // have, so nobody gets thrown into the VS intro over a half-loaded
  // arena. Both waits self-cap, so a stalled CDN can't strand the match.
  void Promise.all([assetGate.whenLoaded(), preloadImages(WEAPON_ICON_IMG_URLS)]).then(() => {
    console.log("[fps] all assets loaded — reporting client_ready");
    client?.sendClientReady();
  });

})();
